// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { FormFieldClickable, FormFieldIcon } from "@insite/mobius/FormField";
import { IconWrapper } from "@insite/mobius/Icon";
import { fontConfig } from "@stoneage/XDL/constants";
import { xBaseTheme } from "@stoneage/XDL/types";
import { color, ellipsis, getSystemColor } from "@stoneage/XDL/Utilities";
import { css } from "styled-components";

const PlaceholderSelector = `
        &::placeholder,
        &::-webkit-input-placeholder
`;

export const placeholderStyle = css`
    ${PlaceholderSelector} {
        color: ${getSystemColor("common.ui6")};
        font-size: ${fontConfig.smallSingleLine.size}px;
        line-height: ${fontConfig.smallSingleLine.lineHeight};
        font-weight: ${fontConfig.weight.regular};
        font-family: ${fontConfig.fontFamily};
        font-style: italic;
        letter-spacing: 0;
        max-width: 100%;
    }
`;

export default {
    defaultProps: {
        mergeCss: true,
        border: "rounded",
        sizeVariant: "default",
        backgroundColor: "common.ui3",
        labelProps: {
            mergeCss: true,
            ...fontConfig.xSmallSingleLine,
            weight: fontConfig.weight.regular,
            transform: "capitalize",
            color: "text",
            css: css`
                min-height: initial;
                letter-spacing: 0;
                display: flex;
                align-items: center;
                & .hint-wrapper {
                    margin-top: -1px;
                    display: flex;
                    flex-grow: 1;
                }
            `,
        },
        errorProps: {
            mergeCss: true,
            ...fontConfig.xSmallSingleLine,
            weight: fontConfig.weight.regular,
            color: "danger",
            css: css`
                min-height: initial;
                letter-spacing: 0;
            `,
        },
        hintProps: {
            mergeCss: true,
            ...fontConfig.xSmallSingleLine,
            weight: fontConfig.weight.regular,
            color: "text.main",
            css: css`
                min-height: initial;
                letter-spacing: 0;
            `,
        },
        cssOverrides: {
            formInputWrapper: css`
                width: ${({ labelPosition }: any) => (labelPosition === "left" ? "auto" : "100%")};
                input,
                textarea,
                select {
                    &:not(.react-datetime-picker__inputGroup__input) {
                        ${placeholderStyle}
                        &:disabled {
                            ${PlaceholderSelector} {
                                color: ${color("text.main")};
                                font-style: normal;
                            }
                        }
                        &:not(:disabled):not(:focus):hover {
                            border: 1px solid ${color("primary")};
                        }
                        &:not(:disabled):focus {
                            border: 1px solid ${color("secondary")};
                        }
                    }
                }
                input[aria-autocomplete="list"] {
                    ${PlaceholderSelector} {
                        font-style: normal;
                    }
                }

                ${FormFieldIcon} {
                    background: transparent;
                }

                ${FormFieldClickable} {
                    ${FormFieldIcon} {
                        right: 0;
                        background: transparent;
                    }
                    &:hover {
                        ${FormFieldIcon} {
                            color: ${color("primary")};
                        }
                    }
                }
                input,
                select {
                    &:not(.react-datetime-picker__inputGroup__input) {
                        ${ellipsis(1)}
                        text-overflow: ellipsis;
                    }
                }
                input,
                textarea {
                    box-shadow: inset 0 1px 0 0 ${color("common.ui9", 0.1)};
                    resize: vertical;
                }

                &[data-size="large"] {
                    && input {
                        padding: 0 16px 0 16px;
                    }

                    && ${IconWrapper} {
                        padding: 10px;
                    }
                }

                .clickable-wrapper {
                    position: absolute;
                    top: 0;
                    right: 0;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                    padding: 0 8px;
                    .divider {
                        width: 1px;
                        height: 24px;
                        background-color: ${color("common.ui5")};
                    }
                    & > ${FormFieldClickable} {
                        position: relative;
                        padding: 0;
                        width: 14px;
                        & ${FormFieldIcon} {
                            padding: 0;
                            width: auto;
                        }
                    }
                }
            `,
            formField: css`
                ${({ labelPosition }: any) => {
                    if (labelPosition === "left") {
                        return css`
                            align-items: baseline;
                        `;
                    }
                }}
            `,
        },
    },
} as xBaseTheme["formField"];
